import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const InstitutionCard = ({ institution, onViewDetails, onToggleFavorite, isFavorite }) => {
  const [imageError, setImageError] = useState(false);

  const formatFees = (fees) => {
    if (fees === 0) return 'Gratuit';
    return `${fees?.toLocaleString('fr-MA')} MAD/an`;
  };

  const getTypeColor = (type) => {
    const colors = {
      'universite-publique': 'bg-blue-100 text-blue-800',
      'universite-privee': 'bg-purple-100 text-purple-800',
      'ecole-ingenieur': 'bg-green-100 text-green-800',
      'ecole-commerce': 'bg-orange-100 text-orange-800',
      'institut-specialise': 'bg-indigo-100 text-indigo-800',
      'ecole-art': 'bg-pink-100 text-pink-800',
      'formation-professionnelle': 'bg-gray-100 text-gray-800'
    };
    return colors?.[type] || 'bg-gray-100 text-gray-800';
  };

  const getTypeLabel = (type) => {
    const labels = {
      'universite-publique': 'Université Publique',
      'universite-privee': 'Université Privée',
      'ecole-ingenieur': 'École d\'Ingénieur',
      'ecole-commerce': 'École de Commerce',
      'institut-specialise': 'Institut Spécialisé',
      'ecole-art': 'École d\'Art',
      'formation-professionnelle': 'Formation Pro'
    };
    return labels?.[type] || type;
  };

  const getDifficultyColor = (difficulty) => {
    const colors = {
      'facile': 'text-green-600',
      'moyen': 'text-orange-600',
      'difficile': 'text-red-600'
    };
    return colors?.[difficulty] || 'text-gray-600';
  };

  const getDifficultyLabel = (difficulty) => {
    const labels = {
      'facile': 'Facile',
      'moyen': 'Moyen',
      'difficile': 'Difficile'
    };
    return labels?.[difficulty] || difficulty;
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 hover:shadow-interactive transition-smooth">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <div className="flex items-center space-x-2 mb-2">
            <span className={`px-2 py-1 text-xs font-medium rounded-full ${getTypeColor(institution?.type)}`}>
              {getTypeLabel(institution?.type)}
            </span>
            {institution?.isNew && (
              <span className="px-2 py-1 text-xs font-medium bg-accent text-accent-foreground rounded-full">
                Nouveau
              </span>
            )}
          </div>
          <h3 className="text-lg font-semibold text-foreground mb-1">{institution?.name}</h3>
          <div className="flex items-center text-sm text-muted-foreground">
            <Icon name="MapPin" size={14} className="mr-1" />
            <span>{institution?.city}</span>
          </div>
        </div>
        <button
          onClick={() => onToggleFavorite(institution?.id)}
          className="p-2 hover:bg-muted rounded-lg transition-smooth"
          aria-label={isFavorite ? 'Retirer des favoris' : 'Ajouter aux favoris'}
        >
          <Icon 
            name={isFavorite ? 'Heart' : 'Heart'} 
            size={20} 
            className={isFavorite ? 'text-red-500 fill-current' : 'text-muted-foreground'} 
          />
        </button>
      </div>
      {/* Institution Image */}
      <div className="mb-4 overflow-hidden rounded-lg h-48">
        <Image
          src={institution?.image}
          alt={institution?.name}
          className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
        />
      </div>
      {/* Programs */}
      <div className="mb-4">
        <h4 className="text-sm font-medium text-foreground mb-2">Programmes populaires</h4>
        <div className="flex flex-wrap gap-1">
          {institution?.programs?.slice(0, 3)?.map((program, index) => (
            <span 
              key={index}
              className="px-2 py-1 text-xs bg-muted text-muted-foreground rounded"
            >
              {program}
            </span>
          ))}
          {institution?.programs?.length > 3 && (
            <span className="px-2 py-1 text-xs text-primary">
              +{institution?.programs?.length - 3} autres
            </span>
          )}
        </div>
      </div>
      {/* Key Information */}
      <div className="space-y-3 mb-4">
        {/* Admission Requirements */}
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Bac acceptés:</span>
          <div className="flex flex-wrap gap-1">
            {institution?.acceptedBacTypes?.slice(0, 2)?.map((bac, index) => (
              <span key={index} className="text-xs bg-primary/10 text-primary px-2 py-1 rounded">
                {bac}
              </span>
            ))}
            {institution?.acceptedBacTypes?.length > 2 && (
              <span className="text-xs text-primary">+{institution?.acceptedBacTypes?.length - 2}</span>
            )}
          </div>
        </div>

        {/* Admission Difficulty */}
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Difficulté d'admission:</span>
          <span className={`text-sm font-medium ${getDifficultyColor(institution?.admissionDifficulty)}`}>
            {getDifficultyLabel(institution?.admissionDifficulty)}
          </span>
        </div>

        {/* Previous Year Threshold */}
        {institution?.previousThreshold && (
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Seuil 2023:</span>
            <span className="text-sm font-medium text-foreground">{institution?.previousThreshold}/20</span>
          </div>
        )}

        {/* Fees */}
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Frais annuels:</span>
          <span className="text-sm font-medium text-foreground">{formatFees(institution?.fees)}</span>
        </div>
      </div>
      {/* Features */}
      {(institution?.hasScholarships || institution?.hasInternships || institution?.hasExchange) && (
        <div className="mb-4">
          <div className="flex flex-wrap gap-2">
            {institution?.hasScholarships && (
              <div className="flex items-center text-xs text-success">
                <Icon name="Award" size={12} className="mr-1" />
                <span>Bourses</span>
              </div>
            )}
            {institution?.hasInternships && (
              <div className="flex items-center text-xs text-primary">
                <Icon name="Briefcase" size={12} className="mr-1" />
                <span>Stages</span>
              </div>
            )}
            {institution?.hasExchange && (
              <div className="flex items-center text-xs text-secondary">
                <Icon name="Globe" size={12} className="mr-1" />
                <span>Échange</span>
              </div>
            )}
          </div>
        </div>
      )}
      {/* Actions */}
      <div className="flex space-x-2">
        <Button
          variant="default"
          onClick={() => onViewDetails(institution)}
          iconName="Eye"
          iconPosition="left"
          fullWidth
        >
          Voir détails
        </Button>
        <Button
          variant="outline"
          onClick={() => window.open(`https://www.google.com/maps/search/${encodeURIComponent(institution?.name + ' ' + institution?.city)}`, '_blank')}
          iconName="MapPin"
          size="icon"
        />
      </div>
    </div>
  );
};

export default InstitutionCard;